import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Eye, Pause, Play, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MesAnnoncesPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [myAds, setMyAds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      await fetchMyAds();
    } catch (error) {
      toast.error('Vous devez être connecté');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyAds = async () => {
    try {
      const response = await axios.get(`${API}/acquirer-ads/my`, { withCredentials: true });
      setMyAds(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const toggleStatus = async (adId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      await axios.patch(`${API}/acquirer-ads/${adId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(newStatus === 'active' ? 'Annonce réactivée' : 'Annonce mise en pause');
      fetchMyAds();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deleteAd = async (adId) => {
    if (!confirm('Supprimer cette annonce ?')) return;
    try {
      await axios.delete(`${API}/acquirer-ads/${adId}`, { withCredentials: true });
      toast.success('Annonce supprimée');
      fetchMyAds();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-bg">
      <Header
        currentUser={user}
        acquirerAdsCount={0}
        onLoginClick={() => {}}
        onAboutClick={() => {}}
        onCreateAdClick={() => navigate('/create-search')}
        onLogout={async () => {
          await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
          navigate('/');
          window.location.reload();
        }}
      />

      <div className="max-w-7xl mx-auto px-2 md:px-4 pb-12">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-6 md:p-8 shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-blue-600">📝 Mes Annonces</h2>
            <Button onClick={() => navigate('/create-search')} className="rounded-full bg-green-600">
              ➕ Nouvelle annonce
            </Button>
          </div>

          {myAds.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">Aucune annonce</p>
              <Button onClick={() => navigate('/create-search')} className="rounded-full">
                ➕ Créer ma première annonce
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {myAds.map((ad) => (
                <div key={ad.ad_id} className="bg-white border border-gray-200 rounded-2xl p-4 md:p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-800 mb-2">{ad.title}</h3>
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                          {ad.property_type}
                        </span>
                        <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold">
                          📍 {ad.location}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          ad.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {ad.status === 'active' ? '✓ Active' : '⏸ En pause'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">💰 Jusqu'à {ad.max_budget.toLocaleString('fr-FR')} €</p>
                      <p className="text-sm text-gray-600">📬 {ad.proposals_received} proposition(s) reçue(s)</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button onClick={() => navigate(`/ad/${ad.ad_id}`)} size="sm" variant="outline" className="rounded-full">
                        <Eye className="w-4 h-4 mr-1" /> Voir
                      </Button>
                      <Button onClick={() => toggleStatus(ad.ad_id, ad.status)} size="sm" variant="outline" className="rounded-full">
                        {ad.status === 'active' ? <Pause className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                        {ad.status === 'active' ? 'Pause' : 'Activer'}
                      </Button>
                      <Button onClick={() => deleteAd(ad.ad_id)} size="sm" variant="destructive" className="rounded-full">
                        <Trash2 className="w-4 h-4 mr-1" /> Suppr.
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MesAnnoncesPage;
